import { useApolloClient } from '@apollo/client'
import { withTheme } from '@emotion/react'
import styled from '@emotion/styled'
import {
  AssignmentInd,
  Brightness4,
  Brightness7,
  Menu as MenuIcon,
  Refresh,
  Settings,
} from '@mui/icons-material'
import {
  AppBar as MuiAppBar,
  Breadcrumbs,
  Grid2 as Grid,
  IconButton as MuiIconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material'
import React from 'react'
import { Link, useLocation } from 'react-router-dom'

import { usePermissions } from '@/hooks/usePermissions'
import useTheme from '@/hooks/useTheme'
import useT from '@/i18n/useT'

import NavbarUserDropdown from './NavbarUserDropdown'

const AppBar = styled(MuiAppBar)`
  background: ${props => props.theme.header.background};
  color: ${props => props.theme.header.color};
`

const IconButton = styled(MuiIconButton)`
  svg {
    width: 22px;
    height: 22px;
  }
`

type NavbarProps = {
  onDrawerToggle: React.MouseEventHandler<HTMLElement>
  breadcrumbs?: React.ReactNode
  pageTitle?: string
}

const PATHS_TO_HIDE_REFRESH = [
  '/admin/profile',
  '/admin/plan-management',
  '/store',
]

const Navbar: React.FC<NavbarProps> = ({
  onDrawerToggle,
  breadcrumbs,
  pageTitle,
}) => {
  const location = useLocation()
  const { t } = useT()
  const { theme, setTheme } = useTheme()
  const muiTheme = useMuiTheme()
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'))
  const [settingsAnchorEl, setSettingsAnchorEl] =
    React.useState<null | HTMLElement>(null)

  const shouldHideRefreshButton = PATHS_TO_HIDE_REFRESH.some(path =>
    location.pathname.includes(path),
  )

  const client = useApolloClient()
  const { hasPermission } = usePermissions()
  const canManageStoreAssignments = hasPermission('CAN_ASSIGN_USER_STORES')

  const handleRefresh = () => {
    client.resetStore()
  }

  const toggleTheme = () => {
    setTheme(theme === 'DARK' ? 'LIGHT' : 'DARK')
    setSettingsAnchorEl(null)
  }

  const handleSettingsClick = (event: React.MouseEvent<HTMLElement>) => {
    setSettingsAnchorEl(event.currentTarget)
  }

  const handleSettingsClose = () => {
    setSettingsAnchorEl(null)
  }

  return (
    <>
      <AppBar
        position={isMobile ? 'static' : 'sticky'}
        elevation={1}
        sx={{
          width: '100%',
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Toolbar sx={{ maxWidth: 1440, width: '100%' }}>
          <Grid
            container
            alignItems="center"
            sx={{
              width: '100%',
            }}
          >
            <Grid sx={{ display: { xs: 'block', md: 'none' } }}>
              <IconButton
                aria-label={t('navbar.menu.openDrawer')}
                onClick={onDrawerToggle}
                size="large"
              >
                <MenuIcon />
              </IconButton>
            </Grid>
            <Grid>
              {pageTitle ? (
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  sx={theme => ({
                    color:
                      theme.palette.mode === 'dark'
                        ? theme.palette.common.white
                        : theme.palette.secondary.main,
                  })}
                >
                  {pageTitle}
                </Typography>
              ) : (
                <Breadcrumbs>{breadcrumbs}</Breadcrumbs>
              )}
            </Grid>
            <Grid size="grow" />
            <Grid>
              {shouldHideRefreshButton ? null : (
                <IconButton onClick={handleRefresh}>
                  <Tooltip title={t('common.refreshOnPage')}>
                    <Refresh />
                  </Tooltip>
                </IconButton>
              )}
              <IconButton onClick={handleSettingsClick}>
                <Tooltip title={t('navbar.settings')}>
                  <Settings />
                </Tooltip>
              </IconButton>
              <Menu
                anchorEl={settingsAnchorEl}
                open={Boolean(settingsAnchorEl)}
                onClose={handleSettingsClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem onClick={toggleTheme}>
                  <ListItemIcon>
                    {theme === 'DARK' ? (
                      <Brightness7 fontSize="small" />
                    ) : (
                      <Brightness4 fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText>
                    {theme === 'DARK'
                      ? t('navbar.theme.light')
                      : t('navbar.theme.dark')}
                  </ListItemText>
                </MenuItem>
                {canManageStoreAssignments ? (
                  <MenuItem
                    component={Link}
                    to="/admin/store-assignments"
                    onClick={handleSettingsClose}
                  >
                    <ListItemIcon>
                      <AssignmentInd fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('navbar.storeAssignments')}</ListItemText>
                  </MenuItem>
                ) : null}
              </Menu>
              <NavbarUserDropdown />
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
    </>
  )
}

export default withTheme(Navbar)
